import { useAssistantRefresh } from '../hooks/useAssistantRefresh';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  engagementTypeLabel,
  MEETING_REPEAT_LABEL,
  MEETING_RESPONSE_LABEL,
  MEETING_STATUS_LABEL,
  MeetingStatus,
  type CalendarEventKind,
  type CalendarEventSummary,
  type MeetingResponse,
  type RecipientGroupSummary,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { calendarApi } from '../api/calendar.api';
import { recipientGroupApi } from '../api/recipient-group.api';
import { cellStyle, downloadXlsx, HEADER_STYLE, XLSX_FILL, type StyleGrid } from '../lib/xlsx';
import { SidePanel } from '../components/SidePanel';
import { CalendarEventForm } from '../components/CalendarEventForm';
import { MeetingPopover, type PopoverAnchor } from '../components/MeetingPopover';
import { TimeSplitCard } from '../components/TimeSplitCard';
import { MeetingNotes } from '../components/MeetingNotes';
import { MeetingFilesPanel } from '../components/MeetingFilesPanel';
import { MeetingBriefingPanel } from '../components/MeetingBriefing';
import { notifyCalendarEventsChanged } from '../lib/calendarEvents';

/**
 * Calendar - personal, one-off meetings and reminders. Deliberately NOT
 * Tasks: no assignment, no completion, no status - just "when, where, and
 * who else is invited". An event lives on its owner's calendar; anyone
 * invited sees it on theirs too, read-only unless they own it.
 */

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_CHIPS_PER_DAY = 3;

/** Week view geometry: one hour of the day-planner grid, in pixels. */
const HOUR_PX = 56;
const HOURS = Array.from({ length: 24 }, (_, h) => h);

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
/** Sunday 00:00 of the week the date falls in - weeks run Sun -> Sat. */
function startOfWeek(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay());
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
/** Local YYYY-MM-DD - the key events are bucketed by for their day cell. */
function dateKey(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}
function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
function whenLabel(startsAt: string, endsAt: string): string {
  const longDay = (iso: string) => new Date(iso).toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
  /* A meeting may now span days. Printing only the start date beside both
     times read as a 30-minute slot however far the end was pushed out. */
  if (dateKey(new Date(startsAt)) !== dateKey(new Date(endsAt))) {
    return `${longDay(startsAt)} ${timeLabel(startsAt)} - ${longDay(endsAt)} ${timeLabel(endsAt)}`;
  }
  return `${longDay(startsAt)} · ${timeLabel(startsAt)} - ${timeLabel(endsAt)}`;
}
function hourLabel(h: number): string {
  return new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: 'numeric' });
}
/** "30 August – 05 September, 2026" - the week header, Teams-style. */
function weekTitle(weekStart: Date): string {
  const end = addDays(weekStart, 6);
  const opts = { day: '2-digit', month: 'long' } as const;
  return `${weekStart.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}, ${end.getFullYear()}`;
}
/** Minutes past local midnight - where an event sits in its day column. */
/**
 * Where this instant sits inside ONE given day, in minutes.
 *
 * A multi-day meeting is drawn once per day it covers, so on every day
 * but its first it begins at midnight, and on every day but its last it
 * runs to midnight. Without the clamp, day two of a three-day meeting
 * measured 17:00-17:30 and drew a half-hour block.
 */
function minutesWithinDay(iso: string, dayKey: string): number {
  const d = new Date(iso);
  const k = dateKey(d);
  if (k === dayKey) return d.getHours() * 60 + d.getMinutes();
  // Earlier than this day -> midnight; later -> the end of it.
  return k < dayKey ? 0 : 1440;
}

/** Every day key an event touches, first to last. */
function daysCovered(startsAt: string, endsAt: string): string[] {
  const first = new Date(startsAt);
  const last = new Date(endsAt);
  /* Ending exactly at midnight belongs to the day before - otherwise a
     meeting to 00:00 paints an empty strip on the following day. */
  if (last.getHours() === 0 && last.getMinutes() === 0) last.setMinutes(last.getMinutes() - 1);

  const keys: string[] = [];
  const cursor = new Date(first.getFullYear(), first.getMonth(), first.getDate());
  const end = new Date(last.getFullYear(), last.getMonth(), last.getDate());
  /* Capped. A bad end date (a typo'd year) would otherwise push tens of
     thousands of entries into the map and hang the render. */
  for (let i = 0; cursor <= end && i < 366; i += 1) {
    keys.push(dateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys.length > 0 ? keys : [dateKey(first)];
}
/** ISO-8601 week number, for the "W36" badge beside the range label. */
function isoWeekNo(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}
/** Chips are tinted by STATUS: tentative yellow, confirmed green,
 *  cancelled red, rescheduled light purple. (Ownership still shows as the
 *  Owner/Invited badge in the day lists.) */
const STATUS_CHIP: Record<MeetingStatus, string> = {
  TENTATIVE: 'border-amber-500 bg-amber-100 text-amber-900',
  CONFIRMED: 'border-emerald-500 bg-emerald-100 text-emerald-900',
  CANCELLED: 'border-red-500 bg-red-100 text-red-900',
  RESCHEDULED: 'border-purple-400 bg-purple-100 text-purple-900',
};

/** RSVP chips - the invitee's answer, shown to organiser and invitees alike. */
const RESPONSE_CHIP: Record<MeetingResponse, string> = {
  PENDING: 'bg-slate-100 text-slate-500',
  ACCEPTED: 'bg-emerald-100 text-emerald-800',
  DECLINED: 'bg-red-100 text-red-700',
  TENTATIVE: 'bg-amber-100 text-amber-800',
};

/**
 * Engagements are coloured by TYPE; meetings by STATUS. Two different
 * questions, two different scales - and no hue is shared between them, so
 * "is this a meeting or an engagement?" is answerable at a glance in the
 * All tab, while the Engagements tab still separates rally from town hall.
 *
 * Every step used here is declared for BOTH themes in index.css. A step
 * that is not declared falls back to Tailwind's light palette and turns
 * into a pale smudge on the dark ground - which is exactly what these
 * chips did before.
 */
/*
 * ONE COLOUR FOR EVERY ENGAGEMENT.
 *
 * These were three maps keyed by the old four-value enum - a hue each for
 * Rally, Town hall and Press event. Types are rows an organisation owns
 * now and can add to freely, and there is nowhere for a new type's colour
 * to come from: a hand-written map cannot cover a type invented after it
 * was written, so every custom type silently took OTHER's grey while the
 * legend went on claiming engagements were coloured by type.
 *
 * So engagements are one family colour, the way meetings-by-status is its
 * own scale. The TYPE still shows, as a short tag on the chip and in full
 * on the popover - it is just carried by text rather than hue.
 *
 * If per-type colour is wanted later it belongs on the type row, not in a
 * constant here: a `colour` column and a picker beside the name.
 */
const ENGAGEMENT_CHIP = 'border-violet-600 bg-violet-100 text-violet-900';

/** The legend dot - the same hue as the chips. */
const ENGAGEMENT_DOT = 'bg-violet-600';

/**
 * A short tag, so the type survives a chip too narrow to read.
 *
 * DERIVED, not looked up: initials of the label, which works for a type
 * nobody had thought of when this was written. "Town hall" stays TH;
 * "Rally" becomes RAL (was RLY) and "Press event" PE (was PR), which is
 * the small price for a rule that covers everything.
 */
function engagementTag(code: string | null | undefined): string {
  const words = engagementTypeLabel(code).split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'ENG';
  if (words.length === 1) return (words[0] ?? '').slice(0, 3).toUpperCase();
  return words.slice(0, 3).map((w) => w.charAt(0)).join('').toUpperCase();
}



function chipClasses(ev: CalendarEventSummary): string {
  return ev.kind === 'ENGAGEMENT'
    // border-l-4 (against the meetings' border-l-2) makes the kind visible
    // even to someone who cannot separate the hues at all.
    ? `border-l-4 ${ENGAGEMENT_CHIP}`
    : STATUS_CHIP[ev.status];
}

const STATUS_DOT: Record<MeetingStatus, string> = {
  TENTATIVE: 'bg-amber-500',
  CONFIRMED: 'bg-emerald-500',
  CANCELLED: 'bg-red-500',
  RESCHEDULED: 'bg-purple-400',
};

/** On top of the tint: cancelled struck out and dimmed, tentative italic. */
function statusClasses(ev: CalendarEventSummary): string {
  if (ev.status === 'CANCELLED') return 'line-through opacity-60';
  // A meeting YOU declined reads like a cancelled one - struck through and
  // faded - because for you it is no longer a commitment, even though it
  // still runs for everyone else.
  if (ev.myResponse === 'DECLINED') return 'line-through opacity-50';
  if (ev.myResponse === 'TENTATIVE') return 'opacity-80 border-dashed';
  if (ev.status === 'TENTATIVE') return 'italic';
  return '';
}

/** Chip click -> the peek card, anchored to the chip's own rectangle. */
function anchorOf(el: HTMLElement): PopoverAnchor {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/** 405 -> "6h 45m"; 60 -> "1h"; 45 -> "45m". */
function durationLabel(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/**
 * Greedy lane layout for one day's events: overlapping events split the
 * column's width between them; non-overlapping events reuse lane 0 and get
 * the full width back.
 */
function layoutDay(dayEvents: CalendarEventSummary[], dayKey: string): Array<{
  event: CalendarEventSummary;
  top: number;
  height: number;
  lane: number;
  lanes: number;
}> {
  const sorted = [...dayEvents].sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  type Placed = {
    event: CalendarEventSummary;
    top: number; height: number; lane: number; lanes: number;
  };
  const placed: Placed[] = [];

  /*
   * WIDTH IS PER OVERLAP CLUSTER, NOT PER DAY.
   *
   * This used to end with `for (const p of placed) p.lanes = laneEnds.length`
   * - the day's widest point applied to every event in it. So two meetings
   * clashing at 09:00 squeezed the 18:00 one to half width with nothing
   * beside it, which reads as a half-drawn chip rather than a layout rule.
   *
   * A cluster is a maximal run of events joined by overlap. Events only
   * share width with things they actually collide with, and a lone event
   * is always full width however busy the rest of the day is.
   */
  let cluster: Placed[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -1;

  const closeCluster = () => {
    const lanes = Math.max(laneEnds.length, 1);
    for (const p of cluster) p.lanes = lanes;
    cluster = [];
    laneEnds = [];
    clusterEnd = -1;
  };

  for (const event of sorted) {
    const start = minutesWithinDay(event.startsAt, dayKey);
    const end = Math.max(minutesWithinDay(event.endsAt, dayKey), start + 30);

    // Starts after everything so far ended: nothing here can collide with it.
    if (cluster.length > 0 && start >= clusterEnd) closeCluster();

    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    if (lane === -1) {
      laneEnds.push(end);
      lane = laneEnds.length - 1;
    } else {
      laneEnds[lane] = end;
    }
    clusterEnd = Math.max(clusterEnd, end);

    const item: Placed = {
      event,
      top: (start / 60) * HOUR_PX,
      height: Math.max(((end - start) / 60) * HOUR_PX - 2, 20),
      lane,
      lanes: 0,
    };
    cluster.push(item);
    placed.push(item);
  }
  closeCluster();

  return placed;
}

export default function CalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  /** WEEK is the calendar's home view; Month stays one click away. */
  const [view, setView] = useState<'week' | 'month'>('week');
  const [monthCursor, setMonthCursor] = useState(() => startOfMonth(new Date()));
  const [weekCursor, setWeekCursor] = useState(() => startOfWeek(new Date()));
  /** The sidebar's mini-month has its own cursor - browsing it never moves
   *  the big view until a day is actually clicked. */
  const [miniCursor, setMiniCursor] = useState(() => startOfMonth(new Date()));
  const [events, setEvents] = useState<CalendarEventSummary[]>([]);
  // ---- filters (client-side over the loaded window) ----
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<MeetingStatus[]>([]);
  const [ownership, setOwnership] = useState<'all' | 'mine' | 'invited'>('all');
  /** Meetings, public engagements, or both. */
  const [kindTab, setKindTab] = useState<'ALL' | CalendarEventKind>('ALL');
  /** Organiser OR attendee must be one of these ids. */
  const [peopleFilter, setPeopleFilter] = useState<string[]>([]);
  /**
   * Recipient groups. Matches on RECORDED PROVENANCE - the groups that
   * actually filled a meeting's attendee list when it was made - not on
   * "everyone in the group happens to be invited". So it never reports a
   * meeting that merely involved those people, and it reports nothing at
   * all for meetings created before provenance was recorded.
   */
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  const [groups, setGroups] = useState<RecipientGroupSummary[]>([]);
  /**
   * ORGANISATION. The calendar is the one screen that spans every org the
   * person belongs to, so it is also the one that needs this filter. The
   * options come from the server, not from the events on screen: an org
   * with nothing booked this week must still be selectable, or "why is my
   * other company missing" becomes a support question.
   */
  const [orgFilter, setOrgFilter] = useState<string[]>([]);
  const [myOrgs, setMyOrgs] = useState<Array<{ orgId: string; orgName: string }>>([]);
  const [respondingId, setRespondingId] = useState<string | null>(null);
  /** The Teams-style peek card: which meeting, and the chip it hangs off. */
  const [peek, setPeek] = useState<{ event: CalendarEventSummary; anchor: PopoverAnchor } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<CalendarEventSummary | null>(null);
  const [creatingOnDate, setCreatingOnDate] = useState<string | null>(null);
  /** HH:mm start pre-fill when a week-view slot was clicked. */
  const [creatingTime, setCreatingTime] = useState<string | null>(null);
  // Date key of a day whose "+N more" was clicked - lists every event that
  // day so an overflowing chip is never permanently hidden.
  const [dayDetail, setDayDetail] = useState<string | null>(null);
  const [deepLinkError, setDeepLinkError] = useState<string | null>(null);
  /** Export feedback: ok = green success toast, warn = amber "nothing to
   *  export" toast. Auto-dismissed after a few seconds. */
  const [flash, setFlash] = useState<{ kind: 'ok' | 'warn'; text: string } | null>(null);
  useEffect(() => {
    if (!flash) return;
    const timer = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(timer);
  }, [flash]);

  const gridStart = useMemo(() => addDays(monthCursor, -monthCursor.getDay()), [monthCursor]);
  const gridDays = useMemo(
    () => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)),
    [gridStart],
  );
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekCursor, i)),
    [weekCursor],
  );
  const miniStart = useMemo(() => addDays(miniCursor, -miniCursor.getDay()), [miniCursor]);
  const miniDays = useMemo(
    () => Array.from({ length: 42 }, (_, i) => addDays(miniStart, i)),
    [miniStart],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = (view === 'week' ? weekCursor : gridStart).toISOString();
      const to = (view === 'week'
        ? addDays(weekCursor, 7)
        : addDays(gridStart, 42)
      ).toISOString();
      setEvents(await calendarApi.list(from, to));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your calendar');
    } finally {
      setLoading(false);
    }
  }, [view, weekCursor, gridStart]);

  useEffect(() => { void load(); }, [load]);

  // The AI chat can change data server-side; refetch when it says so.
  useAssistantRefresh(load);

  /** The week planner opens at the CURRENT time when today is on screen -
   *  the now-line lands about a third of the way down, so what's next is
   *  in view. Browsing another week falls back to the 8 AM working morning. */
  const weekScrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = weekScrollRef.current;
    if (view !== 'week' || !el) return;
    const now = new Date();
    const todayVisible = weekDays.some((d) => dateKey(d) === dateKey(now));
    if (todayVisible) {
      const nowTop = ((now.getHours() * 60 + now.getMinutes()) / 60) * HOUR_PX;
      el.scrollTo({ top: Math.max(0, nowTop - el.clientHeight / 3) });
    } else {
      el.scrollTo({ top: 8 * HOUR_PX });
    }
  }, [view, weekDays]);

  // A notification for a meeting invite/reminder links here as
  // /calendar?event=<id> - open that event's detail once, then drop the
  // param so refreshing the page does not keep reopening it.
  useEffect(() => {
    const eventId = searchParams.get('event');
    if (!eventId) return;
    setDeepLinkError(null);
    calendarApi
      .byId(eventId)
      .then((event) => {
        setSelected(event);
        setMonthCursor(startOfMonth(new Date(event.startsAt)));
        setWeekCursor(startOfWeek(new Date(event.startsAt)));
      })
      .catch((err) => {
        setDeepLinkError(err instanceof ApiError ? err.message : 'Could not open that event');
      })
      .finally(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('event');
        setSearchParams(next, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Everyone who appears on the loaded window, organisers and attendees
   * alike - the filter's options. Derived rather than fetched: these are
   * exactly the people you actually have meetings with.
   */
  const people = useMemo(() => {
    const map = new Map<string, string>();
    for (const ev of events) {
      map.set(ev.ownerId, ev.ownerName);
      for (const a of ev.attendees) map.set(a.userId, a.displayName);
    }
    return [...map].map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);

  /**
   * The filters run over the WINDOW ALREADY LOADED - no refetch, so typing
   * is instant. Every view below reads `filtered`, the export included, so
   * what you export is what you see.
   */
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return events.filter((ev) => {
      if (needle) {
        const haystack = [ev.title, ev.location ?? '', ev.description ?? ''].join(' ').toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      if (kindTab !== 'ALL' && ev.kind !== kindTab) return false;
      if (statusFilter.length > 0 && !statusFilter.includes(ev.status)) return false;
      if (ownership === 'mine' && !ev.isOwner) return false;
      if (ownership === 'invited' && ev.isOwner) return false;
      if (peopleFilter.length > 0) {
        const involved = [ev.ownerId, ...ev.attendees.map((a) => a.userId)];
        if (!peopleFilter.some((id) => involved.includes(id))) return false;
      }
      if (groupFilter.length > 0) {
        const built = ev.recipientGroupIds ?? [];
        if (!groupFilter.some((id) => built.includes(id))) return false;
      }
      if (orgFilter.length > 0 && !orgFilter.includes(ev.orgId)) return false;
      return true;
    });
  }, [events, search, statusFilter, ownership, peopleFilter, groupFilter, orgFilter, kindTab]);

  const filtersActive =
    search.trim() !== '' || statusFilter.length > 0 || ownership !== 'all'
    || peopleFilter.length > 0 || groupFilter.length > 0 || orgFilter.length > 0
    || kindTab !== 'ALL';

  function clearFilters() {
    setSearch('');
    setStatusFilter([]);
    setOwnership('all');
    setPeopleFilter([]);
    setGroupFilter([]);
    setOrgFilter([]);
    setKindTab('ALL');
  }

  /* The filter's options. One call, once - groups change rarely, and a
     failure here must not take the calendar down with it. */
  useEffect(() => {
    let cancelled = false;
    recipientGroupApi.list()
      .then((list) => { if (!cancelled) setGroups(list); })
      .catch(() => undefined);
    /* Same reasoning as the form: a failure here hides the Organisation
       filter, which must not be confused with "you are in one org". The
       console line is the only honest signal a filter can give. */
    calendarApi.myOrgs()
      .then((list) => { if (!cancelled) setMyOrgs(list); })
      .catch((err) => console.warn('[calendar] could not load organisations:', err));
    return () => { cancelled = true; };
  }, []);

  /** Checklist writes return the refreshed event - swap it in, no reload. */
  function applyUpdated(updated: CalendarEventSummary) {
    setEvents((current) => current.map((e) => (e.id === updated.id ? updated : e)));
    setSelected((cur) => (cur && cur.id === updated.id ? updated : cur));
    setPeek((cur) => (cur && cur.event.id === updated.id ? { ...cur, event: updated } : cur));
  }

  async function checklistAction(
    action: () => Promise<CalendarEventSummary>,
  ) {
    try {
      applyUpdated(await action());
    } catch (err) {
      setFlash({
        kind: 'warn',
        text: err instanceof ApiError ? err.message : 'That checklist change did not save.',
      });
    }
  }

  /** An invitee answering. Replaces the event in place - no full reload. */
  async function respond(id: string, response: Exclude<MeetingResponse, 'PENDING'>) {
    setRespondingId(id);
    try {
      const updated = await calendarApi.respond(id, response);
      setEvents((current) => current.map((e) => (e.id === updated.id ? updated : e)));
      setSelected((cur) => (cur && cur.id === updated.id ? updated : cur));
      setPeek((cur) => (cur && cur.event.id === updated.id ? { ...cur, event: updated } : cur));
      setFlash({ kind: 'ok', text: `Answered: ${MEETING_RESPONSE_LABEL[response]}.` });
    } catch (err) {
      setFlash({
        kind: 'warn',
        text: err instanceof ApiError ? err.message : 'Could not send your answer.',
      });
    } finally {
      setRespondingId(null);
    }
  }

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEventSummary[]>();
    for (const ev of filtered) {
      /* Once per day it covers, not once at its start - a meeting that
         runs Fri to Mon belongs in all four columns. */
      for (const key of daysCovered(ev.startsAt, ev.endsAt)) {
        const list = map.get(key) ?? [];
        list.push(ev);
        map.set(key, list);
      }
    }
    for (const list of map.values()) list.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    return map;
  }, [filtered]);

  /** The sidebar's "Up next": what's still ahead of you today, first 3. */
  const upNext = useMemo(() => {
    const now = Date.now();
    return (byDay.get(dateKey(new Date())) ?? [])
      .filter((ev) => new Date(ev.endsAt).getTime() >= now)
      .slice(0, 3);
  }, [byDay]);

  /**
   * What the sidebar analytics measure: the filtered events that fall
   * inside the range on screen. The month grid loads 42 days, which spills
   * into the neighbouring months - counting those would answer a question
   * nobody asked.
   */
  const visibleForStats = useMemo(() => {
    const start = (view === 'week' ? weekCursor : monthCursor).getTime();
    const end = (view === 'week'
      ? addDays(weekCursor, 7)
      : addMonths(monthCursor, 1)
    ).getTime();
    return filtered.filter((ev) => {
      const t = new Date(ev.startsAt).getTime();
      return t >= start && t < end;
    });
  }, [filtered, view, weekCursor, monthCursor]);


  function upsert(event: CalendarEventSummary) {
    setEvents((current) => {
      const withoutIt = current.filter((e) => e.id !== event.id);
      return [...withoutIt, event];
    });
    setSelected(null);
    setCreatingOnDate(null);
    setCreatingTime(null);
    // A repeating meeting just created its whole series server-side; only a
    // reload shows the sibling occurrences in this window.
    if (event.repeat !== 'NONE') void load();
    // The reminder toast only re-arms on mount/focus otherwise - a fresh or
    // just-moved remind_at needs to be picked up right away, not whenever
    // this tab next happens to regain OS-level focus.
    notifyCalendarEventsChanged();
  }

  function afterDelete(id: string) {
    setEvents((current) => current.filter((e) => e.id !== id));
    setSelected(null);
  }

  /**
   * The visible range (this week or this month grid) as one .xlsx - exactly
   * the events on screen, so the export can never disagree with the view.
   * Nothing on screen -> an amber toast instead of an empty workbook.
   */
  function exportToExcel() {
    const rangeLabel = view === 'week' ? 'this week' : 'this month';
    if (filtered.length === 0) {
      setFlash({ kind: 'warn', text: `No meetings or events to export for ${rangeLabel}.` });
      return;
    }
    try {
      const sorted = [...filtered].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
      const day = (iso: string) =>
        new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
      const grid = [
        ['Title', 'Date', 'Starts', 'Ends', 'Duration', 'Status', 'Repeat', 'Location',
         'Organiser', 'My role', 'Attendees', 'Reminder', 'Description'],
        ...sorted.map((ev) => [
          ev.title,
          day(ev.startsAt),
          timeLabel(ev.startsAt),
          timeLabel(ev.endsAt),
          durationLabel(Math.max(0, (new Date(ev.endsAt).getTime() - new Date(ev.startsAt).getTime()) / 60_000)),
          MEETING_STATUS_LABEL[ev.status],
          MEETING_REPEAT_LABEL[ev.repeat],
          ev.location ?? '',
          ev.ownerName,
          ev.isOwner ? 'Mine' : 'Invited',
          ev.attendees.map((a) => a.displayName).join(', '),
          ev.remindAt ? `${day(ev.remindAt)} ${timeLabel(ev.remindAt)}` : '',
          ev.description ?? '',
        ]),
      ];
      const header = grid[0] ?? [];
      // Bold header; rows tinted by STATUS - the same colours the chips use:
      // tentative amber, confirmed green, cancelled red, rescheduled purple.
      const STATUS_FILL: Record<MeetingStatus, number> = {
        TENTATIVE: XLSX_FILL.AMBER,
        CONFIRMED: XLSX_FILL.GREEN,
        CANCELLED: XLSX_FILL.RED,
        RESCHEDULED: XLSX_FILL.PURPLE,
      };
      const styles: StyleGrid = [
        header.map(() => HEADER_STYLE),
        ...sorted.map((ev) =>
          header.map((_, col) =>
            col === 0 ? cellStyle(undefined, true) : cellStyle(STATUS_FILL[ev.status]),
          ),
        ),
      ];
      const colWidths = header.map((_, col) => {
        const longest = grid.reduce((max, row) => Math.max(max, (row[col] ?? '').length), 0);
        return Math.min(60, Math.max(10, Math.round(longest * 1.1) + 2));
      });
      downloadXlsx(`Meetings-${new Date().toISOString().slice(0, 10)}`, 'Meetings', grid, styles, {
        colWidths,
        freezeHeader: true,
        autoFilter: true,
      });
      setFlash({
        kind: 'ok',
        text: `Exported ${sorted.length} ${sorted.length === 1 ? 'event' : 'events'} for ${rangeLabel} to Excel.`,
      });
    } catch {
      setFlash({ kind: 'warn', text: 'Could not export the Meetings - try again.' });
    }
  }

  const todayKey = dateKey(new Date());
  const monthLabel = monthCursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const panelOpen = selected !== null || creatingOnDate !== null || dayDetail !== null;
  const dayDetailEvents = dayDetail ? byDay.get(dayDetail) ?? [] : [];
  const dayDetailLabel = dayDetail
    ? new Date(`${dayDetail}T00:00:00`).toLocaleDateString(undefined, {
        weekday: 'long', month: 'long', day: 'numeric',
      })
    : '';
  const panelTitle = creatingOnDate
    ? 'Create Meetings'
    : selected
      ? selected.title
      : dayDetailLabel;

  return (
    <div className="mx-auto px-8 py-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Meetings</h1>
          <p className="mt-1 text-sm text-slate-500">
            Your own meetings and reminders - separate from tasks. Invite others and they will see it too.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* The visible week/month as a real Excel workbook. */}
          <button
            type="button"
            onClick={exportToExcel}
            disabled={loading}
            title="Download the events on screen as an Excel file"
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            ↓ Export Excel
          </button>
          <button
            type="button"
            onClick={() => { setCreatingTime(null); setCreatingOnDate(todayKey); }}
            className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-brand-700"
          >
            Create Meetings
          </button>
        </div>
      </header>

      {flash && (
        <div
          role="status"
          className={[
            'mt-5 flex items-start justify-between gap-3 rounded-lg px-3.5 py-2.5 text-sm ring-1',
            flash.kind === 'ok'
              ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
              : 'bg-amber-50 text-amber-800 ring-amber-200',
          ].join(' ')}
        >
          <span>{flash.text}</span>
          <button type="button" onClick={() => setFlash(null)} aria-label="Dismiss"
                  className={flash.kind === 'ok' ? 'text-emerald-600 hover:text-emerald-800' : 'text-amber-600 hover:text-amber-800'}>&times;</button>
        </div>
      )}

      {deepLinkError && (
        <div role="alert" className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-red-50 px-3.5 py-2.5 text-sm text-red-700 ring-1 ring-red-100">
          <span>{deepLinkError}</span>
          <button type="button" onClick={() => setDeepLinkError(null)} aria-label="Dismiss"
                  className="text-red-600 hover:text-red-800">&times;</button>
        </div>
      )}

      {/* ---- filters: instant, over the window already loaded ---- */}
      <div className="mt-5 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
        {/* Meetings vs public engagements. Both sit in one calendar on
            purpose - that is what makes a rally clash with a meeting. */}
        <div className="flex items-center gap-0.5 rounded-lg bg-slate-100 p-1">
          {([['ALL', 'All'], ['MEETING', 'Meetings'], ['ENGAGEMENT', 'Engagements']] as const).map(
            ([key, label]) => (
              <button key={key} type="button" onClick={() => setKindTab(key)}
                className={[
                  'rounded-md px-2.5 py-1 text-xs font-semibold transition',
                  kindTab === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
                ].join(' ')}>
                {label}
              </button>
            ),
          )}
        </div>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title, location or notes"
          className="min-w-[14rem] flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm outline-none transition focus:border-brand-500"
        />

        <div className="flex items-center gap-0.5 rounded-lg bg-slate-100 p-1">
          {([['all', 'All'], ['mine', 'Organised by me'], ['invited', 'Invited']] as const).map(
            ([key, label]) => (
              <button key={key} type="button" onClick={() => setOwnership(key)}
                className={[
                  'rounded-md px-2.5 py-1 text-xs font-semibold transition',
                  ownership === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
                ].join(' ')}>
                {label}
              </button>
            ),
          )}
        </div>

        {/* Status: click to toggle, none selected means every status. */}
        <div className="flex items-center gap-1">
          {(Object.keys(STATUS_DOT) as MeetingStatus[]).map((st) => {
            const on = statusFilter.includes(st);
            return (
              <button key={st} type="button"
                onClick={() => setStatusFilter((cur) =>
                  cur.includes(st) ? cur.filter((x) => x !== st) : [...cur, st])}
                className={[
                  'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition',
                  on ? 'border-brand-400 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50',
                ].join(' ')}>
                <span className={`h-2 w-2 rounded-full ${STATUS_DOT[st]}`} />
                {MEETING_STATUS_LABEL[st]}
              </button>
            );
          })}
        </div>

        {people.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              const id = e.target.value;
              if (id) setPeopleFilter((cur) => (cur.includes(id) ? cur : [...cur, id]));
            }}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none transition focus:border-brand-500"
          >
            <option value="">+ Person…</option>
            {people
              .filter((x) => !peopleFilter.includes(x.id))
              .map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        )}

        {peopleFilter.map((id) => (
          <button key={id} type="button"
            onClick={() => setPeopleFilter((cur) => cur.filter((x) => x !== id))}
            className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-200">
            {people.find((x) => x.id === id)?.name ?? id}
            <span aria-hidden>&times;</span>
          </button>
        ))}

        {/* Organisation. Hidden for the single-org majority: a filter with
            one possible value filters nothing. */}
        {myOrgs.length > 1 && (
          <select
            value=""
            onChange={(e) => {
              const id = e.target.value;
              if (id) setOrgFilter((cur) => (cur.includes(id) ? cur : [...cur, id]));
            }}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none transition focus:border-brand-500"
          >
            <option value="">Organisation…</option>
            {myOrgs
              .filter((o) => !orgFilter.includes(o.orgId))
              .map((o) => <option key={o.orgId} value={o.orgId}>{o.orgName}</option>)}
          </select>
        )}

        {orgFilter.map((id) => (
          <button key={id} type="button"
            onClick={() => setOrgFilter((cur) => cur.filter((x) => x !== id))}
            className="flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 transition hover:bg-indigo-200">
            {myOrgs.find((o) => o.orgId === id)?.orgName ?? id}
            <span aria-hidden>&times;</span>
          </button>
        ))}

        {/* Groups. Hidden entirely when the org has none - an empty picker
            is just a thing to click that does nothing. */}
        {groups.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              const id = e.target.value;
              if (id) setGroupFilter((cur) => (cur.includes(id) ? cur : [...cur, id]));
            }}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none transition focus:border-brand-500"
          >
            <option value="">Group…</option>
            {groups
              .filter((g) => !groupFilter.includes(g.id))
              .map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}

        {groupFilter.map((id) => (
          <button key={id} type="button"
            onClick={() => setGroupFilter((cur) => cur.filter((x) => x !== id))}
            className="flex items-center gap-1 rounded-full bg-brand-100 px-2.5 py-1 text-[11px] font-semibold text-brand-700 transition hover:bg-brand-200">
            {groups.find((g) => g.id === id)?.name ?? id}
            <span aria-hidden>&times;</span>
          </button>
        ))}

        {groupFilter.length > 0 && filtered.length === 0 && (
          <span className="text-xs text-amber-700">
            No meeting here was built from that group. Only meetings created
            by picking the group are recorded against it.
          </span>
        )}

        {filtersActive && (
          <>
            <span className="text-xs text-slate-400">
              {filtered.length} of {events.length}
            </span>
            <button type="button" onClick={clearFilters}
              className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">
              Clear
            </button>
          </>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button type="button"
            onClick={() => (view === 'week'
              ? setWeekCursor((w) => addDays(w, -7))
              : setMonthCursor((m) => addMonths(m, -1)))}
            aria-label={view === 'week' ? 'Previous week' : 'Previous month'}
            className="grid h-8 w-8 place-items-center rounded-lg border border-slate-300 text-slate-500 transition hover:bg-slate-50">
            &lsaquo;
          </button>
          <button type="button"
            onClick={() => (view === 'week'
              ? setWeekCursor((w) => addDays(w, 7))
              : setMonthCursor((m) => addMonths(m, 1)))}
            aria-label={view === 'week' ? 'Next week' : 'Next month'}
            className="grid h-8 w-8 place-items-center rounded-lg border border-slate-300 text-slate-500 transition hover:bg-slate-50">
            &rsaquo;
          </button>
          <button type="button"
            onClick={() => {
              setWeekCursor(startOfWeek(new Date()));
              setMonthCursor(startOfMonth(new Date()));
            }}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50">
            Today
          </button>
        </div>
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <span>{view === 'week' ? weekTitle(weekCursor) : monthLabel}</span>
          {view === 'week' && (
            // ISO weeks run Mon-Sun, so this Sun-Sat week's number comes
            // from its Thursday - the day that owns the ISO week.
            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-slate-500">
              W{isoWeekNo(addDays(weekCursor, 4))}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-3">
          {/*
            The legend follows the TAB, because the two kinds are coloured by
            different things: a meeting by its status, an engagement by its
            type. Showing both scales at once on the All tab would be eight
            swatches of noise, so All names the two families instead and the
            per-value detail appears once you narrow to one of them.
          */}
          <div className="hidden items-center gap-3 text-[11px] font-medium text-slate-500 sm:flex">
            {kindTab === 'ENGAGEMENT' ? (
              /* One swatch, not one per type: engagements share a colour
                 and carry their type as text. */
              <span className="flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${ENGAGEMENT_DOT}`} />
                Engagements
              </span>
            ) : kindTab === 'MEETING' ? (
              (Object.keys(STATUS_DOT) as MeetingStatus[]).map((s) => (
                <span key={s} className="flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${STATUS_DOT[s]}`} /> {MEETING_STATUS_LABEL[s]}
                </span>
              ))
            ) : (
              <>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Meetings (by status)
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-violet-600" />
                  Engagements
                </span>
              </>
            )}
          </div>
          {/* Week first and initially active; Month is the option. */}
          <div className="flex items-center gap-0.5 rounded-lg bg-slate-100 p-1">
            {(['week', 'month'] as const).map((mode) => (
              <button key={mode} type="button" onClick={() => setView(mode)}
                className={[
                  'rounded-md px-3 py-1.5 text-xs font-semibold capitalize transition',
                  view === mode
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700',
                ].join(' ')}>
                {mode}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Big view on the left; navigator + at-a-glance cards on the right
          (sidebar appears on wide screens only, like the design canvas). */}
      <div className="mt-4 grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_17rem]">
      <div className="min-w-0">
      {error ? (
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-12 text-center">
          <p className="text-sm text-red-600">{error}</p>
          <button type="button" onClick={() => void load()}
            className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50">
            Try again
          </button>
        </div>
      ) : view === 'week' ? (
        /* ---- WEEK: a Sun->Sat day planner, Teams-style ---- */
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div ref={weekScrollRef} className="max-h-[34rem] overflow-y-auto">
            {/* The day header lives INSIDE the scroller (sticky) so its
                columns share the scrollbar with the grid below - outside
                it, the body columns are one scrollbar-width narrower and
                the two grids drift out of line. */}
            <div className="sticky top-0 z-20 grid grid-cols-[3.5rem_repeat(7,minmax(0,1fr))] border-b border-slate-200 bg-slate-50">
              <div />
              {weekDays.map((day) => {
                const isToday = dateKey(day) === todayKey;
                return (
                  <div
                    key={dateKey(day)}
                    className={[
                      // Every cell carries the 2px top border so today's
                      // brand-coloured one doesn't push its text down.
                      'border-l border-t-2 border-slate-200 px-3 py-2',
                      isToday ? 'border-t-brand-500 bg-brand-50/60' : 'border-t-transparent',
                    ].join(' ')}
                  >
                    <p className={`text-[10px] font-semibold uppercase tracking-wide ${isToday ? 'text-brand-600' : 'text-slate-400'}`}>
                      {day.toLocaleDateString(undefined, { weekday: 'short' })}
                    </p>
                    <p className={`font-mono text-lg font-semibold leading-tight ${isToday ? 'text-brand-600' : 'text-slate-800'}`}>
                      {String(day.getDate()).padStart(2, '0')}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-[3.5rem_repeat(7,minmax(0,1fr))]">
              {/* Hour gutter */}
              <div>
                {HOURS.map((h) => (
                  /*
                    slate-200, not slate-100. In LIGHT mode slate-100 is
                    #f1f5f9 - near-white on white, so the grid vanished.
                    index.css inverts the slate scale under .dark, where
                    slate-100 became a mid-dark grey and looked fine, which
                    is why this only showed up in light mode.
                  */
                  <div key={h} style={{ height: HOUR_PX }} className="relative border-b border-slate-200">
                    <span className="absolute -top-2 right-1.5 bg-white px-0.5 font-mono text-xs text-slate-500">
                      {h === 0 ? '' : hourLabel(h)}
                    </span>
                  </div>
                ))}
              </div>

              {/* One column per day; events absolutely positioned by time. */}
              {weekDays.map((day) => {
                const key = dateKey(day);
                const isToday = key === todayKey;
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const placed = layoutDay(byDay.get(key) ?? [], key);
                const now = new Date();
                const nowTop = ((now.getHours() * 60 + now.getMinutes()) / 60) * HOUR_PX;
                return (
                  <div
                    key={key}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const hour = Math.min(23, Math.max(0, Math.floor((e.clientY - rect.top) / HOUR_PX)));
                      setCreatingTime(`${String(hour).padStart(2, '0')}:00`);
                      setCreatingOnDate(key);
                    }}
                    className={[
                      'relative cursor-pointer border-l border-slate-200',
                      // Today gets a light brand wash; weekends a grey one -
                      // the working days read as the "active" surface.
                      isToday ? 'bg-brand-50/40' : isWeekend ? 'bg-slate-50/50' : '',
                    ].join(' ')}
                    style={{ height: 24 * HOUR_PX }}
                  >
                    {HOURS.map((h) => (
                      <div key={h} style={{ height: HOUR_PX }} className="border-b border-slate-200" />
                    ))}

                    {placed.map(({ event, top, height, lane, lanes }) => (
                      <button
                        key={event.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPeek({ event, anchor: anchorOf(e.currentTarget) });
                        }}
                        title={`${event.title} · ${timeLabel(event.startsAt)} - ${timeLabel(event.endsAt)}${event.hasConflict ? ' · overlaps another meeting' : ''}`}
                        style={{
                          top,
                          height,
                          left: `${(lane / lanes) * 100}%`,
                          width: `${100 / lanes}%`,
                        }}
                        className={[
                          // Title only - the hour grid already says WHEN, and
                          // the full times live in the hover tooltip.
                          'absolute overflow-hidden rounded-md border-l-2 px-1.5 py-1 text-left text-xs leading-tight transition hover:opacity-90',
                          chipClasses(event),
                          statusClasses(event),
                          // A clash is louder than the status tint: a red
                          // outline, so an overlap is visible at a glance
                          // without opening anything.
                          event.hasConflict ? 'ring-2 ring-red-500' : '',
                        ].join(' ')}
                      >
                        <span className="block truncate font-semibold">
                          {event.kind === 'ENGAGEMENT' && (
                            <span className="mr-1 opacity-70">
                              {engagementTag(event.engagementType)}
                            </span>
                          )}
                          {event.title}
                        </span>
                      </button>
                    ))}

                    {/* The "now" line, on today's column only. */}
                    {isToday && (
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-brand-500"
                        style={{ top: nowTop }}
                      >
                        <span className="absolute -left-1 -top-[5px] h-2 w-2 animate-pulse rounded-full bg-brand-500" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
            {WEEKDAY_LABELS.map((label) => (
              <div key={label} className="px-2.5 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {label}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {gridDays.map((day) => {
              const key = dateKey(day);
              const inMonth = day.getMonth() === monthCursor.getMonth();
              const isToday = key === todayKey;
              const dayEvents = byDay.get(key) ?? [];
              const overflow = dayEvents.length - MAX_CHIPS_PER_DAY;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setCreatingTime(null); setCreatingOnDate(key); }}
                  className={[
                    'flex min-h-[104px] flex-col items-stretch gap-1 border-b border-r border-slate-200 p-1.5 text-left align-top transition last:border-r-0',
                    inMonth ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/60 hover:bg-slate-50',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'grid h-6 w-6 place-items-center rounded-full text-xs font-medium',
                      isToday
                        ? 'bg-brand-600 text-white'
                        : inMonth ? 'text-slate-700' : 'text-slate-400',
                    ].join(' ')}
                  >
                    {day.getDate()}
                  </span>

                  <span className="flex flex-1 flex-col gap-1">
                    {dayEvents.slice(0, MAX_CHIPS_PER_DAY).map((ev) => (
                      <span
                        key={ev.id}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPeek({ event: ev, anchor: anchorOf(e.currentTarget) });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            setPeek({ event: ev, anchor: anchorOf(e.currentTarget) });
                          }
                        }}
                        className={[
                          'truncate rounded px-1.5 py-0.5 text-left text-xs font-medium transition hover:opacity-80',
                          chipClasses(ev),
                          statusClasses(ev),
                          ev.hasConflict ? 'ring-2 ring-red-500' : '',
                        ].join(' ')}
                        title={`${timeLabel(ev.startsAt)} · ${ev.title}${ev.hasConflict ? ' · overlaps another meeting' : ''}`}
                      >
                        {ev.kind === 'ENGAGEMENT'
                          ? `${engagementTag(ev.engagementType)} `
                          : ''}
                        {timeLabel(ev.startsAt)} {ev.title}
                      </span>
                    ))}
                    {overflow > 0 && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setDayDetail(key); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setDayDetail(key); }
                        }}
                        className="rounded px-1.5 text-left text-[11px] font-medium text-slate-400 transition hover:text-slate-600"
                      >
                        +{overflow} more
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loading && <p className="mt-3 text-xs text-slate-400">Loading...</p>}
      </div>

      {/* Wide screens: a right-hand rail. Below xl the same three cards
          reflow into a row UNDER the calendar instead of disappearing -
          smaller laptops lose the layout, not the information. */}
      <aside className="grid items-start gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:flex xl:flex-col">
        {/* Mini-month navigator - browse with its own arrows; clicking a
            day jumps the big view (week AND month cursors) to it. */}
        <div className="rounded-xl border border-slate-200 bg-white p-3 w-full">
          <div className="flex items-center justify-between px-1">
            <p className="text-xs font-semibold text-slate-700">
              {miniCursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </p>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setMiniCursor((m) => addMonths(m, -1))}
                aria-label="Previous month"
                className="grid h-6 w-6 place-items-center rounded text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
                &lsaquo;
              </button>
              <button type="button" onClick={() => setMiniCursor((m) => addMonths(m, 1))}
                aria-label="Next month"
                className="grid h-6 w-6 place-items-center rounded text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
                &rsaquo;
              </button>
            </div>
          </div>
          <div className="mt-2 grid grid-cols-7 text-center text-[10px] font-semibold text-slate-400">
            {WEEKDAY_LABELS.map((l) => (
              <span key={l}>{l[0]}</span>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-y-0.5">
            {miniDays.map((day) => {
              const key = dateKey(day);
              const inMonth = day.getMonth() === miniCursor.getMonth();
              const isToday = key === todayKey;
              const hasEvents = byDay.has(key);
              const inBigWeek =
                view === 'week' && day >= weekCursor && day < addDays(weekCursor, 7);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setWeekCursor(startOfWeek(day));
                    setMonthCursor(startOfMonth(day));
                  }}
                  title={day.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                  className={[
                    'relative mx-auto grid h-7 w-7 place-items-center rounded-full text-[11px] transition',
                    isToday
                      ? 'bg-brand-600 font-semibold text-white'
                      : inBigWeek
                        ? 'bg-brand-50 font-medium text-brand-700'
                        : inMonth
                          ? 'text-slate-700 hover:bg-slate-100'
                          : 'text-slate-300 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {day.getDate()}
                  {hasEvents && !isToday && (
                    <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-brand-400" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Up next - the rest of today at a glance. */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 w-full">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Up next</p>
          {upNext.length === 0 ? (
            <p className="mt-2 text-xs text-slate-400">Nothing else on your calendar today.</p>
          ) : (
            <ul className="mt-2 space-y-1">
              {upNext.map((ev) => (
                <li key={ev.id}>
                  <button
                    type="button"
                    onClick={(e) => setPeek({ event: ev, anchor: anchorOf(e.currentTarget) })}
                    className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left transition hover:bg-slate-50"
                  >
                    <span
                      className={`mt-1 h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[ev.status]}`}
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-medium text-slate-800">{ev.title}</span>
                      <span className="block text-[11px] text-slate-400">
                        {timeLabel(ev.startsAt)} - {timeLabel(ev.endsAt)}
                        {ev.location ? ` · ${ev.location}` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/*
          Time-split analytics. Reads `filtered` and the kind tab, so it
          always describes exactly what the grid beside it is showing -
          narrow to one person and the split narrows with it.
        */}
        <TimeSplitCard
          events={visibleForStats}
          tab={kindTab}
          rangeLabel={view === 'week' ? 'This week' : 'This month'}
          onSelectEvent={(ev, el) => setPeek({ event: ev, anchor: anchorOf(el) })}
        />
      </aside>
      </div>

      {peek && (
        <MeetingPopover
          event={peek.event}
          anchor={peek.anchor}
          busy={respondingId === peek.event.id}
          onRespond={(r) => void respond(peek.event.id, r)}
          onToggleChecklistItem={(itemId, done) => void checklistAction(
            () => calendarApi.updateChecklistItem(peek.event.id, itemId, { done }),
          )}
          onAddChecklistItem={(label) => void checklistAction(
            () => calendarApi.addChecklistItem(peek.event.id, label),
          )}
          onRemoveChecklistItem={(itemId) => void checklistAction(
            () => calendarApi.removeChecklistItem(peek.event.id, itemId),
          )}
          onExpand={() => { setSelected(peek.event); setPeek(null); }}
          onClose={() => setPeek(null)}
        />
      )}

      <SidePanel
        open={panelOpen}
        wide
        title={panelTitle}
        description={
          creatingOnDate
            ? 'A one-off meeting or reminder on your calendar.'
            : selected
              ? whenLabel(selected.startsAt, selected.endsAt)
              : dayDetail
                ? `${dayDetailEvents.length} event${dayDetailEvents.length === 1 ? '' : 's'}`
                : undefined
        }
        onClose={() => { setSelected(null); setCreatingOnDate(null); setCreatingTime(null); setDayDetail(null); }}
      >
        {creatingOnDate ? (
          <CalendarEventForm
            defaultDate={creatingOnDate}
            defaultTime={creatingTime ?? undefined}
            onSaved={upsert}
            onCancel={() => { setCreatingOnDate(null); setCreatingTime(null); }}
          />
        ) : selected?.isOwner ? (
          <CalendarEventForm
            initial={selected}
            onSaved={upsert}
            onDeleted={afterDelete}
            onCancel={() => setSelected(null)}
            onEventUpdated={applyUpdated}
          />
        ) : selected ? (
          <div className="space-y-5 px-6 py-5">
            {selected.location && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Location</p>
                <p className="mt-1 text-sm text-slate-700">{selected.location}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Organiser</p>
              <p className="mt-1 text-sm text-slate-700">{selected.ownerName}</p>
            </div>
            {/* Only when the calendar actually spans more than one. For
                everybody else it is a line saying where they already are. */}
            {myOrgs.length > 1 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Organisation</p>
                <p className="mt-1 text-sm text-slate-700">{selected.orgName}</p>
              </div>
            )}
            {selected.hasConflict && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700 ring-1 ring-red-100">
                This overlaps another meeting on your calendar
                {myOrgs.length > 1 ? ', which may be in another organisation' : ''}.
              </p>
            )}

            {/* Your answer. Declining does not remove the meeting - the
                organiser decides that; it tells them where you stand. */}
            {selected.myResponse && selected.status !== 'CANCELLED' && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your answer</p>
                <div className="mt-1.5 flex items-center gap-1.5">
                  {(['ACCEPTED', 'TENTATIVE', 'DECLINED'] as const).map((r) => (
                    <button key={r} type="button"
                      disabled={respondingId === selected.id}
                      onClick={() => void respond(selected.id, r)}
                      className={[
                        'rounded-lg border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50',
                        selected.myResponse === r
                          ? 'border-brand-500 bg-brand-50 text-brand-700'
                          : 'border-slate-300 text-slate-600 hover:bg-slate-50',
                      ].join(' ')}>
                      {MEETING_RESPONSE_LABEL[r]}
                    </button>
                  ))}
                </div>
                {selected.myResponse === 'PENDING' && (
                  <p className="mt-1.5 text-xs text-slate-400">
                    {selected.ownerName} can see whether you have answered.
                  </p>
                )}
              </div>
            )}

            {selected.attendees.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Attendees</p>
                <ul className="mt-1.5 space-y-1">
                  {selected.attendees.map((a) => (
                    <li key={a.userId} className="flex items-center justify-between gap-2 text-sm text-slate-700">
                      <span className="truncate">{a.displayName}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${RESPONSE_CHIP[a.response]}`}>
                        {MEETING_RESPONSE_LABEL[a.response]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {selected.description && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Description</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{selected.description}</p>
              </div>
            )}

            {/* Files, READ-ONLY. An invitee is exactly who the agenda and
                the papers are for - the API has always allowed them to
                download (visible = owner OR attendee); this panel was
                simply never rendered on the invitee's side, so the whole
                section was invisible to the people it exists for. */}
            <MeetingFilesPanel eventId={selected.id} canEdit={false} />

            {/* An invitee gets the same briefing - walking in prepared is
                not an organiser's privilege. History is filtered to what
                THEY can see. */}
            <MeetingBriefingPanel event={selected} />

            {/* Read-only here: the organiser owns the record. An invitee can
                still draft a follow-up - summarising for their own team is
                the same job. */}
            <MeetingNotes event={selected} canEdit={false} onSaved={applyUpdated} />

            <p className="text-xs text-slate-400">
              You were invited to this event, so only {selected.ownerName} can change or cancel it.
            </p>
          </div>
        ) : dayDetail ? (
          <div className="space-y-1 px-6 py-5">
            {dayDetailEvents.map((ev) => (
              <button
                key={ev.id}
                type="button"
                onClick={() => { setDayDetail(null); setSelected(ev); }}
                className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left transition hover:bg-slate-50"
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-slate-800">{ev.title}</span>
                    {ev.hasConflict && (
                      <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-700">
                        Clash
                      </span>
                    )}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {timeLabel(ev.startsAt)} - {timeLabel(ev.endsAt)}
                    {ev.location ? ` · ${ev.location}` : ''}
                    {ev.kind === 'ENGAGEMENT'
                      ? ` · ${engagementTypeLabel(ev.engagementType)}`
                        + (ev.checklist.length > 0
                          ? ` · ${ev.checklist.filter((c) => c.done).length}/${ev.checklist.length} prep done`
                          : '')
                      : ev.isOwner && ev.attendees.length > 0
                        ? ` · ${ev.attendees.filter((a) => a.response === 'ACCEPTED').length}/${ev.attendees.length} accepted`
                        : ''}
                  </span>
                </span>
                <span
                  className={[
                    'shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium',
                    ev.isOwner ? 'bg-emerald-100 text-emerald-800' : 'bg-brand-100 text-brand-800',
                  ].join(' ')}
                >
                  {ev.isOwner ? 'Owner' : 'Invited'}
                </span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => { const d = dayDetail; setDayDetail(null); setCreatingOnDate(d); }}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-500 transition hover:border-brand-300 hover:text-brand-600"
            >
              + Create Meetings on this day
            </button>
          </div>
        ) : null}
      </SidePanel>
    </div>
  );
}
